import React, {
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  globalStyle,
  handleDangerVisible,
  displayTwoDecimalDigits,
  section236HAmountFor,
  section236HFinalAmountFor,
  section236HLabel,
  SECTION_236H_FINAL_LABEL,
  hasSection236H,
  sanitizeDecimalInput,
} from "../../global/Utils";
import { round2, formatTwoDecimals } from "../../global/money";
import {
  appliesFurtherTax,
  furtherTaxNote,
  invoiceFurtherTax,
  isFurtherTaxLine,
  itemFurtherTaxRate,
  lineFurtherTaxRate,
  reconcileFurtherTax,
  withFurtherTax,
  withoutFurtherTaxMarks,
} from "../../global/furtherTax";
import {
  updateInvoices,
  getTransactionWithCompanyId,
  getInvoiceNumber,
  getRateWithTransactionId,
  getSroWithRateId,
  getSroItemsWithSroId,
} from "../../Actions/InvoicesAction";
import { getCompany } from "../../Actions/CompanyAction";
import { getBuyer } from "../../Actions/BuyerAction";
import { getItem } from "../../Actions/ItemAction";
import { useSection236HRate, section236HNote } from "./useSection236HRate";
import { InputField, MoneyInputField } from "../Common/InputField";
import Swal from "sweetalert2";
import { useNavigate, useLocation } from "react-router-dom";
import { isNull, sumBy } from "lodash";
import { CircularProgress } from "@mui/material";
import {
  payloadAccToType,
  retailPriceSalesTax,
  retailPriceError,
  withRetailPriceErrors,
  itemErrorsWithoutRow,
  RETAIL_PRICE_ZERO_ERROR,
} from "./AddInvoiceHelpingFunction";
import Box from "@mui/material/Box";

import { upperCaseInput } from "../../global/textCase";
import { SearchableSelect } from "../Common/SearchableSelect";
import { ItemGridSelect } from "../Common/ItemGridSelect";
import { ScenarioGridSelect } from "../Common/ScenarioGridSelect";
import { invoiceDateError, invoiceDateRange } from "../../global/invoiceDate";
const defaultItem = {
  itemId: "",
  hsCode: "",
  productDescription: "",
  rateValue: "",
  rate: "",
  rateId: "",
  sroId: "",
  uoM: "",
  quantity: 1,
  totalValues: 0,
  valueSalesExcludingST: "",
  unitPrice: 0,
  salesTaxApplicable: 0,
  salesTaxWithheldAtSource: 0,
  extraTax: "",
  furtherTax: 0,
  sroScheduleNo: 0,
  fedPayable: 0,
  discount: 0,
  saleType: "",
  sroItemSerialNo: 0,
  fixedNotifiedValueOrRetailPrice: "",
};

// The stored `rate` is the FBR description, e.g. "18%" or "Exempt". Only the
// percentage ones carry a number; the rest are resolved against rateList once
// it loads (see findRate), so they start empty rather than NaN.
const parseRateValue = (rate) => {
  const num = parseFloat(rate);
  return Number.isNaN(num) ? "" : num;
};

// Find the rateList entry behind a saved item: by id first, then by the
// description ("Exempt" and "0%" share ratE_VALUE 0, so value is the last
// resort only).
const findRate = (rateList, item) =>
  rateList?.find((r) => item?.rateId && String(r.ratE_ID) === String(item.rateId)) ||
  rateList?.find((r) => item?.rate && r.ratE_DESC === item.rate) ||
  rateList?.find(
    (r) =>
      item?.rateValue !== "" &&
      item?.rateValue !== undefined &&
      String(r.ratE_VALUE) === String(item.rateValue),
  );

// Keeps a partially typed number usable ("12.", "12.5") but never lets the
// user enter more than 2 decimal places.
const limitToTwoDecimals = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const match = String(value).match(/^\d*(\.\d{0,2})?/);
  return match ? match[0] : "";
};

// SN008 and SN027 are retail-price based scenarios: sales tax is charged on
// the fixedNotifiedValueOrRetailPrice entered by the user, not on the sale
// value, and the row total follows from it. Kept in step with the add screen -
// without it this screen showed the sale-value tax while submitting the
// retail-price one, so a 5 x 100 line at 18% displayed 900 and filed 90.
const RETAIL_PRICE_SCENARIOS = ["SN008", "SN027"];

const isRetailPriceScenario = (transactionType) =>
  RETAIL_PRICE_SCENARIOS.includes(transactionType);

const applyRetailPriceValues = (item) => {
  const valueSalesExcludingST = round2(
    (parseFloat(item?.unitPrice) || 0) * (Number(item?.quantity) || 0),
  );
  const salesTaxApplicable = retailPriceSalesTax(item);
  return {
    ...item,
    valueSalesExcludingST,
    salesTaxApplicable,
    totalValues: round2(valueSalesExcludingST + salesTaxApplicable),
  };
};

// Discount is a deduction from the amount payable, never from the taxable
// value: salesTaxApplicable stays computed on the full sale value (or on the
// fixedNotifiedValueOrRetailPrice under SN008/SN027), so discounting a row
// never lowers the sales tax reported to FBR.
//
// The row's Amount and Sales Tax are rounded to two decimals here too, so the
// figures on screen, the total built from them and what is saved all agree.
const applyDiscount = (item) => {
  const valueSalesExcludingST = round2(item?.valueSalesExcludingST);
  const salesTaxApplicable = round2(item?.salesTaxApplicable);
  return {
    ...item,
    valueSalesExcludingST,
    salesTaxApplicable,
    totalValues: round2(
      valueSalesExcludingST +
        salesTaxApplicable -
        (parseFloat(item?.discount) || 0),
    ),
  };
};

// The fields whose handlers below rewrite a row's totals. The discount is
// re-applied on top of the figures they produce, so it is recalculated for
// exactly that set - rows left alone by those handlers keep their total.
const TOTAL_DRIVING_FIELDS = [
  "itemId",
  "quantity",
  "unitPrice",
  "rateValue",
  "fixedNotifiedValueOrRetailPrice",
  "discount",
];

const EditInvoices = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  // The invoice date may be any day of the last two months, up to today
  // (global/invoiceDate.js).
  const { min: minDate, max: maxDate } = invoiceDateRange();
  const formRef = React.useRef(null);

  const {
    _id,
    transactionType,
    invoiceNumber,
    invoiceType,
    date,
    purchaseOrderNo,
    salesTerm,
    section236G,
    GSection236H,
    FBRInvoiceRefNo,
    debitNoteReason,
    saveInvoiceStatus,
    companyId,
    buyerId,
    scenarioId,
    billNo,
    items,
    subTotal,
    taxTotal,
    grandTotal,
  } = location.state || {};

  const isUpdatingInvoicesList = useSelector(
    (state) => state.InvoicesListReducer?.isUpdatingInvoicesList,
  );
  const rateList = useSelector((state) => state.InvoicesListReducer?.rateList);
  const sroList = useSelector((state) => state.InvoicesListReducer?.sroList);
  const { isGettingSroWithRateIdList, isGettingSroItemsWithSroIdList } =
    useSelector((state) => state.InvoicesListReducer);
  const sroItemsList = useSelector(
    (state) => state.InvoicesListReducer?.sroItemsList,
  );
  const transactionTypeList = useSelector(
    (state) => state.InvoicesListReducer?.transectionTypeListAccToCompanyId,
  );
  const isGettingTransactionTypeAccToComId = useSelector(
    (state) => state.InvoicesListReducer?.isGettingTransactionTypeAccToComId,
  );
  const companiesList = useSelector(
    (state) => state.CompanyListReducer?.companiesList || [],
  );
  const buyerList = useSelector((state) => state.BuyerReducer?.buyerList || []);
  const itemList = useSelector(
    (state) => state.ItemListReducer?.itemList || [],
  );
  const isGettingItemList = useSelector(
    (state) => state.ItemListReducer?.isGettingItemList,
  );

  const [formData, setFormData] = useState({
    transactionType: transactionType,
    invoiceNumber: invoiceNumber,
    invoiceType: invoiceType,
    date: date,
    purchaseOrderNo: purchaseOrderNo,
    salesTerm: salesTerm,
    section236G: section236G,
    GSection236H: GSection236H,
    FBRInvoiceRefNo: FBRInvoiceRefNo,
    // Invoices saved before the field existed have none.
    debitNoteReason: debitNoteReason || "",
    saveInvoiceStatus: saveInvoiceStatus,
    // The invoice arrives here as a row from the list endpoint, which
    // populates companyId and buyerId but NOT scenarioId - so scenarioId is a
    // bare id and `scenarioId?._id` was undefined. scenarioId is validated as
    // required but has no input on this screen, so the save was refused by a
    // field the user could neither see nor fill. Accept either shape.
    companyId: companyId?._id ?? companyId,
    buyerId: buyerId?._id ?? buyerId,
    scenarioId: scenarioId?._id ?? scenarioId,
    billNo: billNo,
    subTotal: subTotal,
    taxTotal: taxTotal,
    grandTotal: grandTotal,
    items: items?.map((value) => {
      return {
        ...value,
        rateValue: parseRateValue(value?.rate),
        itemId: value?.itemId?._id || "",
        unitPrice: value?.quantity
          ? round2(value.valueSalesExcludingST / value.quantity)
          : 0,
      };
    }),
  });

  const itemListAccToCompany = itemList.filter(
    (item) =>
      item?.companyId?._id === (formData.companyId?._id ?? formData.companyId),
  );
  const buyerListAccToUser = buyerList?.filter((item) => item?.createdBy?._id === (companiesList?.find((item) => item?._id === formData?.companyId))?.createdBy?._id);
  // Its "Concat serial number with Product Name" setting decides how the item
  // dropdown names each item.
  const invoiceCompany = companiesList?.find(
    (company) =>
      company?._id === (formData.companyId?._id ?? formData.companyId),
  );
  const [errors, setErrors] = useState({});

  function getCompanyShortCode(name) {
    if (!name) return "";

    const ignoreWords = ["and", "ltd", "inc", "corp", "company"];

    return name
      .split(" ")
      .filter((word) => word && !ignoreWords.includes(word.toLowerCase()))
      .map((word) => word[0].toUpperCase())
      .join("");
  }

  // A 0% rate is a real selection, so "has a rate" has to be an emptiness check
  // rather than a truthiness one - rateValue is the number 0 there.
  const hasRate = (item) =>
    item?.rateValue !== "" &&
    item?.rateValue !== null &&
    item?.rateValue !== undefined;

  /* ------------------ HANDLE INPUT CHANGE ------------------ */

  // The GSection 236H % is the company's own rate for the buyer's standing on
  // the Active Taxpayer List: checked whenever the company or buyer changes.
  const setSection236HRate = useCallback(
    (rate) => setFormData((prev) => ({ ...prev, GSection236H: rate })),
    [],
  );
  const { buyerAtl, applySection236H } = useSection236HRate(setSection236HRate);
  const section236HNoteText =
    formData.companyId && formData.buyerId ? section236HNote(buyerAtl) : "";

  // Further Tax: charged on each line's Amount when the company has it on and
  // the buyer is not Active on the ATL (global/furtherTax.js).
  //
  // An opened invoice keeps its saved figures, as its 236H rate does: only a
  // line the user re-prices is worked out again - until the company or buyer
  // is changed, after which every line follows the new pair. The opened
  // buyer's ATL status is looked up once for this, through its own copy of the
  // 236H hook whose rate goes nowhere, so the saved 236H rate is untouched.
  const keepSavedRate = useCallback(() => {}, []);
  const { buyerAtl: openedBuyerAtl, applySection236H: checkOpenedBuyerAtl } =
    useSection236HRate(keepSavedRate);
  const [furtherTaxFollowsChange, setFurtherTaxFollowsChange] =
    useState(false);
  const furtherTaxAtl = furtherTaxFollowsChange ? buyerAtl : openedBuyerAtl;
  // Until the lists load, the invoice's own (populated) company and buyer.
  const furtherTaxCompany =
    invoiceCompany ||
    (companyId?._id && companyId._id === formData.companyId
      ? companyId
      : undefined);
  const furtherTax = useMemo(
    () => invoiceFurtherTax(furtherTaxCompany, furtherTaxAtl),
    [furtherTaxCompany, furtherTaxAtl],
  );
  const furtherTaxNoteText = furtherTaxNote(furtherTax, furtherTaxAtl);
  const itemsById = useMemo(() => {
    const byId = {};
    itemList.forEach((item) => {
      byId[item._id] = item;
    });
    return byId;
  }, [itemList]);
  const furtherTaxRateOf = useCallback(
    (line) => itemFurtherTaxRate(itemsById[line?.itemId]),
    [itemsById],
  );
  const furtherTaxInScope = useCallback(
    (line) => furtherTaxFollowsChange || isFurtherTaxLine(line),
    [furtherTaxFollowsChange],
  );

  const openedAtlChecked = React.useRef(false);
  useEffect(() => {
    if (openedAtlChecked.current || furtherTaxFollowsChange) return;
    const openedBuyer =
      buyerList.find((buyer) => buyer._id === formData.buyerId) ||
      (buyerId?._id ? buyerId : null);
    if (!appliesFurtherTax(furtherTaxCompany) || !openedBuyer) return;
    openedAtlChecked.current = true;
    checkOpenedBuyerAtl(furtherTaxCompany, openedBuyer);
  });

  // Keeps the Further Tax of the lines in scope in step with the company and
  // the buyer's ATL status, which can arrive after a line was re-priced. Only
  // sets state when a line is actually off.
  useLayoutEffect(() => {
    if (
      !reconcileFurtherTax(
        formData.items,
        furtherTax,
        furtherTaxRateOf,
        furtherTaxInScope,
      )
    ) {
      return;
    }
    setFormData((prev) => {
      const items = reconcileFurtherTax(
        prev.items,
        furtherTax,
        furtherTaxRateOf,
        furtherTaxInScope,
      );
      return items ? { ...prev, items } : prev;
    });
  }, [formData.items, furtherTax, furtherTaxRateOf, furtherTaxInScope]);

  const handleChange = (e) => {
    const { name, value } = upperCaseInput(e.target);

    // HANDLE ITEMS ARRAY
    if (name.startsWith("items")) {
      const match = name.match(/items\[(\d+)\]\.(.+)/);
      const index = Number(match[1]);
      const field = match[2];

      const updatedItems = [...formData.items];

      if (field !== "quantity") {
        updatedItems[index] = {
          ...updatedItems[index],
          [field]: value,
        };
      }

      if (field === "itemId") {
        const selectedItem = itemListAccToCompany.find(
          (item) => item._id === value,
        );
        const selectedTransactionType = transactionTypeList.find(
          (item) => item.scenarioCode === formData.transactionType,
        );
        updatedItems[index] = {
          ...updatedItems[index],
          unitPrice: selectedItem?.unitPrice,
          uoM: selectedItem?.uom,
          // Without this the row kept the previous item's sales tax, and the
          // discount step below rebuilt totalValues from that stale figure.
          salesTaxApplicable:
            selectedTransactionType?.scenarioCode === "SN021" ||
            selectedTransactionType?.scenarioCode === "SN023"
              ? parseFloat(updatedItems[index]?.quantity) *
                parseFloat(updatedItems[index]?.rateValue)
              : parseFloat(
                  selectedItem?.unitPrice *
                    Number(formData?.items[index]?.quantity),
                ) *
                  parseFloat(updatedItems[index]?.rateValue / 100) +
                (selectedTransactionType?.scenarioCode === "SN022"
                  ? 60 * Number(updatedItems[index]?.quantity)
                  : 0),
          valueSalesExcludingST:
            selectedItem?.unitPrice * Number(formData?.items[index]?.quantity),
          totalValues:
            parseFloat(
              selectedItem?.unitPrice *
                Number(formData?.items[index]?.quantity),
            ) +
            (selectedTransactionType?.scenarioCode === "SN021" ||
            selectedTransactionType?.scenarioCode === "SN023"
              ? parseFloat(updatedItems[index]?.quantity) *
                parseFloat(updatedItems[index]?.rateValue)
              : parseFloat(
                  selectedItem?.unitPrice *
                    Number(formData?.items[index]?.quantity),
                ) *
                  parseFloat(updatedItems[index]?.rateValue / 100) +
                (selectedTransactionType?.scenarioCode === "SN022"
                  ? 60 * Number(updatedItems[index]?.quantity)
                  : 0)),
          hsCode: selectedItem?.HSCodeId?.pctCode,
          productDescription: selectedItem?.name,
          saleType: selectedTransactionType?.name,
          // The item master does not require a retail price, so most items
          // carry 0. Overwriting the row with that 0 wiped a price the user had
          // already entered and then blocked the save, because SN008/SN027
          // require a retail price above 0. Only take the item's price when it
          // actually has one; otherwise keep what the row already carries.
          fixedNotifiedValueOrRetailPrice:
            Number(selectedItem?.retailPrice) > 0
              ? selectedItem.retailPrice
              : updatedItems[index]?.fixedNotifiedValueOrRetailPrice || "",
        };
      }

      if (field === "quantity") {
        const selectedTransactionType = transactionTypeList.find(
          (item) => item.scenarioCode === formData.transactionType,
        );
        updatedItems[index] = {
          ...updatedItems[index],
          quantity: value === "" ? 0 : value >= 1 ? value : "",
          totalValues:
            (selectedTransactionType?.scenarioCode === "SN021" ||
            selectedTransactionType?.scenarioCode === "SN023"
              ? parseFloat(value) * parseFloat(updatedItems[index]?.rateValue)
              : parseFloat(
                  updatedItems[index]?.unitPrice *
                    Number(value) *
                    (updatedItems[index]?.rateValue / 100),
                ) +
                (selectedTransactionType?.scenarioCode === "SN022"
                  ? 60 * Number(value)
                  : 0)) +
            parseFloat(updatedItems[index]?.unitPrice * Number(value)),
          valueSalesExcludingST: updatedItems[index]?.unitPrice * Number(value),
          salesTaxApplicable:
            selectedTransactionType?.scenarioCode === "SN021" ||
            selectedTransactionType?.scenarioCode === "SN023"
              ? parseFloat(value) * parseFloat(updatedItems[index]?.rateValue)
              : parseFloat(
                  updatedItems[index]?.unitPrice *
                    Number(value) *
                    (updatedItems[index]?.rateValue / 100),
                ) +
                (selectedTransactionType?.scenarioCode === "SN022"
                  ? 60 * Number(value)
                  : 0),
        };
      }
      if (field === "unitPrice") {
        const selectedTransactionType = transactionTypeList.find(
          (item) => item.scenarioCode === formData.transactionType,
        );
        updatedItems[index] = {
          ...updatedItems[index],
          totalValues:
            (selectedTransactionType?.scenarioCode === "SN021" ||
            selectedTransactionType?.scenarioCode === "SN023"
              ? parseFloat(
                  updatedItems[index]?.quantity *
                    updatedItems[index]?.rateValue,
                )
              : parseFloat(
                  updatedItems[index]?.quantity *
                    parseFloat(value || 0) *
                    (updatedItems[index]?.rateValue / 100),
                ) +
                (selectedTransactionType?.scenarioCode === "SN022"
                  ? 60 * Number(updatedItems[index]?.quantity)
                  : 0)) +
            parseFloat(updatedItems[index]?.quantity * parseFloat(value || 0)),
          valueSalesExcludingST:
            updatedItems[index]?.quantity * parseFloat(value || 0),
          salesTaxApplicable:
            selectedTransactionType?.scenarioCode === "SN021" ||
            selectedTransactionType?.scenarioCode === "SN023"
              ? parseFloat(
                  updatedItems[index]?.quantity *
                    updatedItems[index]?.rateValue,
                )
              : parseFloat(
                  updatedItems[index]?.quantity *
                    parseFloat(value || 0) *
                    (updatedItems[index]?.rateValue / 100),
                ) +
                (selectedTransactionType?.scenarioCode === "SN022"
                  ? 60 * Number(updatedItems[index]?.quantity)
                  : 0),
        };
      }
      if (field === "rateValue") {
        // Options are keyed by ratE_ID so rates sharing a value (e.g. "0%"
        // and "Exempt") stay distinguishable; map back to the numeric rate.
        const selectedRate = rateList?.find(
          (r) => String(r.ratE_ID) === String(value),
        );
        const id = selectedRate ? String(selectedRate.ratE_ID) : "";
        const text = selectedRate?.ratE_DESC || "";
        const rateNumber = selectedRate ? selectedRate.ratE_VALUE : "";
        const selectedTransactionType = transactionTypeList.find(
          (item) => item.scenarioCode === formData.transactionType,
        );
        updatedItems[index] = {
          ...updatedItems[index],
          rateValue: rateNumber,
          rate: text,
          rateId: id || "",
          sroId: "",
          sroScheduleNo: "",
          sroItemSerialNo: "",
          salesTaxApplicable:
            selectedTransactionType?.scenarioCode === "SN021" ||
            selectedTransactionType?.scenarioCode === "SN023"
              ? parseFloat(rateNumber * updatedItems[index]?.quantity)
              : updatedItems[index]?.valueSalesExcludingST * (rateNumber / 100) +
                (selectedTransactionType?.scenarioCode === "SN022"
                  ? 60 * Number(updatedItems[index]?.quantity)
                  : 0),
          totalValues:
            (selectedTransactionType?.scenarioCode === "SN021" ||
            selectedTransactionType?.scenarioCode === "SN023"
              ? parseFloat(rateNumber * updatedItems[index]?.quantity)
              : parseFloat(
                  updatedItems[index]?.valueSalesExcludingST * (rateNumber / 100),
                )) +
            parseFloat(updatedItems[index]?.valueSalesExcludingST) +
            (selectedTransactionType?.scenarioCode === "SN022"
              ? 60 * Number(updatedItems[index]?.quantity)
              : 0),
        };
        if (id) {
          dispatch(getSroWithRateId(id, handleDangerVisible));
        }
      }
      if (field === "sroScheduleNo") {
        const id = e.target?.selectedOptions?.[0]?.dataset?.id;
        updatedItems[index] = {
          ...updatedItems[index],
          sroScheduleNo: value,
          sroId: id || "",
          sroItemSerialNo: "",
        };
        if (id) {
          dispatch(getSroItemsWithSroId(id, handleDangerVisible));
        }
      }

      if (field === "discount") {
        updatedItems[index] = {
          ...updatedItems[index],
          discount: limitToTwoDecimals(value),
        };
      }

      if (field === "fixedNotifiedValueOrRetailPrice") {
        updatedItems[index] = {
          ...updatedItems[index],
          fixedNotifiedValueOrRetailPrice: limitToTwoDecimals(value),
        };
      }

      // ST and the totals are driven by the retail price under SN008/SN027, so
      // the row is recalculated after any change that feeds into it.
      const retailPriceRecalculated = isRetailPriceScenario(
        formData.transactionType,
      );
      if (retailPriceRecalculated) {
        updatedItems[index] = applyRetailPriceValues(updatedItems[index]);
      }

      // Applied last so the deduction sits on top of the ST the branches above
      // worked out, leaving that ST untouched. The retail-price recalculation
      // rebuilds totalValues from scratch on *every* field - including the SRO
      // dropdowns, which are picked after the discount is typed - so it has to
      // be put back whenever that ran, not just for the totals-driving fields.
      if (retailPriceRecalculated || TOTAL_DRIVING_FIELDS.includes(field)) {
        updatedItems[index] = applyDiscount(updatedItems[index]);
        // Further Tax goes on top of that total, for a company that charges
        // it; any other company's line comes back exactly as it is.
        updatedItems[index] = withFurtherTax(
          updatedItems[index],
          furtherTax,
          furtherTaxRateOf(updatedItems[index]),
        );
      }

      // SN008 / SN027: a line whose retail price is 0 is flagged as soon as
      // its item is picked or the value is changed, not only on Save.
      const retailPriceCheck =
        (field === "itemId" || field === "fixedNotifiedValueOrRetailPrice") &&
        updatedItems[index]?.itemId
          ? {
              fixedNotifiedValueOrRetailPrice: retailPriceError(
                formData.transactionType,
                updatedItems[index],
              ),
            }
          : {};

      setFormData((prev) => ({ ...prev, items: updatedItems }));
      setErrors((prev) => ({
        ...prev,
        items: {
          ...prev.items,
          [index]: {
            ...prev.items?.[index],
            [field]: "",
            ...retailPriceCheck,
          },
        },
      }));
      return;
    }

    // A percentage: digits with at most two decimal places.
    if (name === "GSection236H") {
      setFormData((prev) => ({ ...prev, [name]: sanitizeDecimalInput(value) }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
      return;
    }

    // TRANSACTION TYPE
    if (name === "transactionType") {
      const transectionTypeId = transactionTypeList.find(
        (item) => item.scenarioCode === value,
      );
      const updatedItems = formData.items.map((item) => ({
        ...item,
        saleType: transectionTypeId?.name,
        rateValue: "",
        rate: "",
        rateId: "",
        sroId: "",
        salesTaxApplicable: "",
        totalValues: "",
        sroScheduleNo: "",
        sroItemSerialNo: "",
      }));
      setFormData((prev) => ({
        ...prev,
        scenarioId: transectionTypeId?._id,
        items: updatedItems,
      }));
      // Lines that already hold an item are checked against the new
      // scenario's retail-price rule straight away.
      setErrors((prev) => ({
        ...prev,
        items: withRetailPriceErrors(value, updatedItems, prev.items),
      }));
      if (transectionTypeId?.transactionTypeId) {
        dispatch(
          getRateWithTransactionId(
            transectionTypeId?.transactionTypeId,
            handleDangerVisible,
          ),
        );
      }
    }

    // COMPANY
    if (name === "companyId" && value !== "") {
      const companyNameRow = companiesList.find((item) => item._id === value);
      dispatch(
        getInvoiceNumber(
          value,
          setFormData,
          getCompanyShortCode(companyNameRow?.companyName),
          // The invoice keeps its original owner, so the sequence is read from
          // them rather than from whoever is doing the editing.
          location.state?.createdBy?._id,
        ),
      );
      dispatch(
        getTransactionWithCompanyId(value, setFormData, handleDangerVisible),
      );
    }

    // The Debit Note Reason belongs to a Debit Note only, so it is emptied as
    // soon as the invoice becomes any other type.
    if (name === "invoiceType" && value !== "Debit Note") {
      setFormData((prev) => ({ ...prev, debitNoteReason: "" }));
      setErrors((prev) => ({ ...prev, debitNoteReason: "" }));
    }

    // GSection 236H % follows the company's rate for the buyer's ATL status
    // (Active / Non-Active) - the saved value is kept until the user changes either of them.
    if (name === "companyId" || name === "buyerId") {
      const company = companiesList.find(
        (item) =>
          item._id === (name === "companyId" ? value : formData.companyId),
      );
      const buyer = buyerList.find(
        (item) => item._id === (name === "buyerId" ? value : formData.buyerId),
      );
      applySection236H(company, buyer);
      // From now on every line's Further Tax follows the new pair.
      setFurtherTaxFollowsChange(true);
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };
  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, { ...defaultItem }],
    }));
  };
  const removeItem = (index) => {
    setFormData((prev) => {
      if (prev.items.length === 1) {
        return {
          ...prev,
          items: [{ ...defaultItem }],
        };
      }

      return {
        ...prev,
        items: prev.items.filter((_, i) => i !== index),
      };
    });
    // Without this a line's error stayed on its row number and showed on the
    // line that moved up into it.
    setErrors((prev) => ({
      ...prev,
      items: itemErrorsWithoutRow(prev.items, index),
    }));
  };

  /* ------------------ VALIDATION ------------------ */
  const validate = () => {
    const newErrors = {};

    /* -------- MAIN REQUIRED FIELDS -------- */
    const requiredFields = [
      "transactionType",
      "scenarioId",
      "invoiceType",
      "date",
      // saveInvoiceStatus is deliberately absent: it is not a field on the
      // form but whichever button was pressed, and handleSubmit always writes
      // it into the payload as `type`. Validating the stale stored value could
      // only ever refuse the save over a field nobody can see or fill.
      "companyId",
      "buyerId",
    ];

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = "This field is required";
      }
    });
    // Only a changed date is held to the last two months: an invoice saved
    // earlier keeps its own date through an edit (global/invoiceDate.js).
    if (formData.date && formData.date !== date && invoiceDateError(formData.date)) {
      newErrors.date = invoiceDateError(formData.date);
    }
    if (Number(formData.GSection236H) > 100) {
      newErrors.GSection236H = "Percentage cannot be more than 100";
    }
    // Required only while it is on screen, i.e. for a Debit Note.
    if (
      formData.invoiceType === "Debit Note" &&
      !formData.debitNoteReason?.trim()
    ) {
      newErrors.debitNoteReason = "This field is required";
    }

    /* -------- ITEM REQUIRED FIELDS -------- */
    const requiredItemFields = [
      "itemId",
      "rateValue",
      "quantity",
      // "totalValues",
      "unitPrice",
      // "salesTaxApplicable",
      ...(sroList?.length > 0 ? ["sroScheduleNo"] : []),
      ...(sroItemsList.length > 0 && sroList?.length > 0
        ? ["sroItemSerialNo"]
        : []),
    ];

    formData.items.forEach((item, index) => {
      requiredItemFields.forEach((field) => {
        if (
          item[field] === "" ||
          item[field] === null ||
          item[field] === undefined
        ) {
          newErrors.items = newErrors.items || {};
          newErrors.items[index] = newErrors.items[index] || {};
          newErrors.items[index][field] = "Required";
        }
      });

      if (
        isRetailPriceScenario(formData.transactionType) &&
        !(Number(item.fixedNotifiedValueOrRetailPrice) > 0)
      ) {
        newErrors.items = newErrors.items || {};
        newErrors.items[index] = newErrors.items[index] || {};
        newErrors.items[index].fixedNotifiedValueOrRetailPrice =
          RETAIL_PRICE_ZERO_ERROR;
      }

      // A discount may wipe out the payable amount but never push it negative.
      const discount = parseFloat(item.discount) || 0;
      const payableBeforeDiscount =
        (parseFloat(item.valueSalesExcludingST) || 0) +
        (parseFloat(item.salesTaxApplicable) || 0);
      if (discount < 0 || discount > payableBeforeDiscount) {
        newErrors.items = newErrors.items || {};
        newErrors.items[index] = newErrors.items[index] || {};
        newErrors.items[index].discount =
          discount < 0 ? "Cannot be negative" : "Cannot exceed the item total";
      }
    });

    setErrors(newErrors);

    /* -------- FINAL RESULT -------- */
    return (
      Object.keys(newErrors).length === 0 &&
      (!newErrors.items || Object.keys(newErrors.items).length === 0)
    );
  };

  const swalHtml = (msg, type, data) => {
    if (type === "success") return `<p>${msg}</p>`;
    else if (type === "error" && isNull(data?.invoiceStatuses))
      return `${data?.error}`;
    else if (type === "error" && !isNull(data?.invoiceStatuses)) {
      return `
          <ul>
            ${data?.invoiceStatuses.map(
              (element) => `<li>${element.error}</li><br />`,
            )}
          </ul>
         `;
    }
  };

  const handelVisibleForUpdateInvoice = (msg, type, data) => {
    Swal.fire({
      title:
        type === "success"
          ? "INVOICE SUBMISSION TO FBR SUCESSFULL"
          : "INVOICE SUBMISSION TO FBR UNSUCESSFUL",
      html: swalHtml(msg, type, data),
      icon: type === "success" ? "success" : "error",
      confirmButtonText: "Okay",
    }).then((result) => {
      if (result.isConfirmed) {
        navigate("/main/view-invoice-list");
      }
    });
  };

  /* ------------- Helper Function -------------- */
  const calculateTotal = (key, list) =>
    round2(list.reduce((sum, item) => sum + Number(item[key] || 0), 0));

  /* ------------------ SUBMIT ------------------ */
  const handleSubmit = (e, type) => {
    e.preventDefault();
    if (!validate()) {
      setTimeout(() => {
        const firstError = document.querySelector(".border-red-500");
        if (firstError) {
          firstError.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          firstError.focus();
        } else {
          // Nothing on screen is marked invalid, so the blocking field is one
          // that is not currently rendered. Without this the button simply did
          // nothing and the invoice looked unsaveable for no visible reason.
          handleDangerVisible(
            "The invoice could not be saved because a required field is missing. Please check the item rows and the invoice details.",
          );
        }
      }, 100);
      return;
    }
    // A re-priced line's Further Tax follows the buyer's ATL status, so it is
    // not saved while that is still being checked.
    if (
      furtherTax.pending &&
      formData.items.some((item) => furtherTaxInScope(item))
    ) {
      handleDangerVisible(
        "The buyer's ATL status is still being checked for Further Tax. Please try again in a moment.",
      );
      return;
    }

    // Transform items
    const updatedItems = formData.items.map((item) => ({
      ...item,
      rateValue: `${item.rateValue}%`, // convert rateValue to "value%"
      fixedNotifiedValueOrRetailPrice: round2(
        item.fixedNotifiedValueOrRetailPrice,
      ),
      discount: round2(item.discount),
    }));

    // Calculate totals
    const subTotal = calculateTotal("valueSalesExcludingST", updatedItems);
    const taxTotal = calculateTotal("salesTaxApplicable", updatedItems);
    const grandTotal = calculateTotal("totalValues", updatedItems);

    // Final payload
    const payload = {
      ...formData,
      items: updatedItems,
      subTotal,
      taxTotal,
      grandTotal,
      GSection236H: Number(formData.GSection236H) || 0,
      FBRInvoiceRefNo:
        formData.invoiceType === "Sale Invoice"
          ? ""
          : formData?.FBRInvoiceRefNo,
      debitNoteReason:
        formData.invoiceType === "Debit Note" ? formData.debitNoteReason : "",
      saveInvoiceStatus: type,
    };

    dispatch(
      updateInvoices(
        _id,
        payloadAccToType(withoutFurtherTaxMarks(payload), {
          includeFurtherTax: furtherTax.managed,
        }),
        handelVisibleForUpdateInvoice,
        handleDangerVisible,
        navigate,
      ),
    );
  };

  // Check For Already Slected Items
  const selectedItemIds = formData.items
    .map((item) => item.itemId)
    .filter(Boolean);

  useEffect(() => {
    const timer = setTimeout(async () => {
      dispatch(getCompany("", "", ""));
      dispatch(getBuyer("", "", ""));
      dispatch(getItem("", "", ""));
      dispatch(
        getTransactionWithCompanyId(
          companyId?._id,
          setFormData,
          handleDangerVisible,
          true,
        ),
      );

      // ✅ ADD THIS: fetch rate list for the existing transactionType
      // You need the transactionTypeId (not scenarioCode) to fetch rates
      // scenarioId._id is the scenario, but getRateWithTransactionId needs transactionTypeId
      if (scenarioId?.transactionTypeId) {
        dispatch(
          getRateWithTransactionId(
            scenarioId.transactionTypeId,
            handleDangerVisible,
          ),
        );
      }
    }, 500);

    if (!location?.state)
      navigate("/main/view-invoice-list", { replace: true });
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [dispatch, location?.state, navigate]);

  // Fires once transactionTypeList loads — fetches rateList for existing transactionType
  useEffect(() => {
    if (!transactionTypeList || transactionTypeList.length === 0) return;
    if (!transactionType) return;

    const matched = transactionTypeList.find(
      (item) => item.scenarioCode === transactionType,
    );

    if (matched?.transactionTypeId) {
      dispatch(
        getRateWithTransactionId(
          matched.transactionTypeId,
          handleDangerVisible,
        ),
      );
    }
    // eslint-disable-next-line
  }, [transactionTypeList]);

  // Refill the SRO dropdowns for the invoice being edited. Items saved with
  // rateId / sroId fetch straight away; older ones have to have their ids
  // resolved back out of the parent list by matching the stored description.
  // Backfill rateValue / rateId for saved items whose rate is not a plain
  // percentage (e.g. "Exempt") once the rate list is available.
  useEffect(() => {
    if (!rateList || rateList.length === 0) return;
    let changed = false;
    const items = formData.items?.map((item) => {
      // Only trust a rateId the current list actually contains - the store can
      // still hold another transaction type's list when this screen opens.
      const idInList = rateList.some(
        (r) => String(r.ratE_ID) === String(item?.rateId),
      );
      if (hasRate(item) && idInList) return item;
      const match = findRate(rateList, item);
      if (!match) return item;
      changed = true;
      return {
        ...item,
        rateValue: match.ratE_VALUE,
        rateId: String(match.ratE_ID),
        rate: item?.rate || match.ratE_DESC,
      };
    });
    if (changed) setFormData((prev) => ({ ...prev, items }));
    // eslint-disable-next-line
  }, [rateList]);

  const hydratedSroRef = React.useRef(false);
  useEffect(() => {
    if (hydratedSroRef.current) return;

    const itemWithRate = formData.items?.find(
      (item) => item?.rateId || item?.rate || item?.rateValue,
    );
    if (!itemWithRate) return;

    let rateId = itemWithRate.rateId;
    if (!rateId) {
      if (!rateList || rateList.length === 0) return;
      rateId = rateList.find(
        (rate) =>
          rate.ratE_DESC === itemWithRate.rate ||
          String(rate.ratE_VALUE) === String(itemWithRate.rateValue),
      )?.ratE_ID;
    }
    if (!rateId) return;

    hydratedSroRef.current = true;
    dispatch(getSroWithRateId(rateId, handleDangerVisible));
    // eslint-disable-next-line
  }, [rateList]);

  const hydratedSroItemsRef = React.useRef(false);
  useEffect(() => {
    if (hydratedSroItemsRef.current) return;

    const itemWithSro = formData.items?.find(
      (item) => item?.sroId || item?.sroScheduleNo,
    );
    if (!itemWithSro) return;

    let sroId = itemWithSro.sroId;
    if (!sroId) {
      if (!sroList || sroList.length === 0) return;
      sroId = sroList.find(
        (sro) => sro.srO_DESC === itemWithSro.sroScheduleNo,
      )?.srO_ID;
    }
    if (!sroId) return;

    hydratedSroItemsRef.current = true;
    dispatch(getSroItemsWithSroId(sroId, handleDangerVisible));
    // eslint-disable-next-line
  }, [sroList]);

  return (
    <>
      {isGettingItemList ? (
        <div className="relative min-h-screen">
          <div className="absolute inset-0 flex items-center justify-center">
            <CircularProgress size={50} color="success" />
          </div>
        </div>
      ) : (
        <div className="w-full min-h-screen flex justify-center py-4">
          <form
            ref={formRef}
            className="w-full space-y-6"
          >
            <h2
              className="text-center font-bold text-xl"
              style={{
                fontFamily: globalStyle.fontFamilyHeading,
                color: globalStyle.curveDarkColor,
              }}
            >
              Edit Invoices
            </h2>

            {/* GRID */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Company Name */}
              <div>
                <label className="block mb-1 font-medium">
                  Company Name<span className="text-red-500">*</span>
                </label>
                <SearchableSelect
                  name="companyId"
                  value={formData.companyId}
                  onChange={handleChange}
                  disabled={!companiesList || companiesList.length === 0}
                  className={`uppercase w-full px-4 py-2 border rounded-md ${
                    errors.companyId ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  {!companiesList || companiesList.length === 0 ? (
                    <option value="">No Companies Available</option>
                  ) : (
                    <>
                      <option value="">Select</option>
                      {companiesList.map((company) => (
                        <option key={company._id} value={company._id}>
                          {company.companyName} / {company?.ntn}
                        </option>
                      ))}
                    </>
                  )}
                </SearchableSelect>
                {errors.companyId && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.companyId}
                  </p>
                )}
              </div>
              {/* Transaction Type */}
              <div>
                <label className="block mb-1 font-medium">
                  Transaction Type<span className="text-red-500">*</span>
                </label>
                {isGettingTransactionTypeAccToComId ? (
                  <div className="flex justify-center items-center h-20">
                    <CircularProgress />
                  </div>
                ) : (
                  <ScenarioGridSelect
                    name="transactionType"
                    value={formData.transactionType}
                    onChange={handleChange}
                    scenarios={transactionTypeList}
                    disabled={
                      !transactionTypeList || transactionTypeList.length === 0
                    }
                    className={`w-full px-4 py-2 border rounded-md ${
                      errors.transactionType
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                  />
                )}
                {errors.transactionType && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.transactionType}
                  </p>
                )}
              </div>
              {/* Invoice Number */}
              <InputField
                label="Invoice Number"
                name="invoiceNumber"
                value={formData.invoiceNumber}
                error={errors.invoiceNumber}
                onChange={handleChange}
                disabled={true}
              />
              {/* Invoice Type */}
              <div>
                <label className="block mb-1 font-medium">
                  Invoice Type <span className="text-red-500">*</span>
                </label>
                <SearchableSelect
                  name="invoiceType"
                  value={formData.invoiceType}
                  onChange={handleChange}
                  className={`uppercase w-full px-4 py-2 border rounded-md ${
                    errors.invoiceType ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  <option value="">Select</option>
                  <option value="Sale Invoice">Sales Invoice</option>
                  {/* <option value="Debit Note">Debit Note</option> */}
                </SearchableSelect>
                {errors.invoiceType && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.invoiceType}
                  </p>
                )}
              </div>
              {/* Invoice Date */}
              <InputField
                label="Invoice Date"
                name="date"
                type="date"
                max={maxDate}
                min={minDate}
                value={formData.date}
                error={errors.date}
                onChange={handleChange}
                isUpperCase={true}
                required
              />
              {/* Buyer */}
              <div>
                <label className="block mb-1 font-medium">
                  Buyer Name<span className="text-red-500">*</span>
                </label>
                <SearchableSelect
                  name="buyerId"
                  value={formData.buyerId}
                  onChange={handleChange}
                  // Wider and taller than the field: buyer names are long.
                  menuMinWidth={560}
                  maxMenuHeight={400}
                  disabled={!buyerListAccToUser || buyerListAccToUser.length === 0}
                  className={`uppercase w-full px-4 py-2 border rounded-md ${
                    errors.buyerId ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  {!buyerListAccToUser || buyerListAccToUser.length === 0 ? (
                    <option value="">No Buyer Available</option>
                  ) : (
                    <>
                      <option value="">Select</option>
                      {buyerListAccToUser.map((buyer) => (
                        <option key={buyer._id} value={buyer._id}>
                          {buyer.businessName}
                        </option>
                      ))}
                    </>
                  )}
                </SearchableSelect>
                {errors.buyerId && (
                  <p className="text-red-500 text-sm mt-1">{errors.buyerId}</p>
                )}
              </div>
              {/* Purchase Order No. */}
              <InputField
                label="PURCHASE ORDER No."
                name="purchaseOrderNo"
                value={formData.purchaseOrderNo}
                error={errors.purchaseOrderNo}
                onChange={handleChange}
              />
              {/* Bill No. */}
              <InputField
                label="Bill No."
                name="billNo"
                value={formData.billNo}
                error={errors.billNo}
                onChange={handleChange}
              />
              {/* Section 236G */}
              <InputField
                label="Section 236G %"
                name="section236G"
                value={formData.section236G}
                error={errors.section236G}
                onChange={handleChange}
              />
              {/* GSEction 236H */}
              <div>
                <InputField
                  label="GSection 236H %"
                  name="GSection236H"
                  value={formData.GSection236H}
                  error={errors.GSection236H}
                  onChange={handleChange}
                />
                {section236HNoteText && (
                  <p role="status" className="text-xs text-gray-600 mt-1">
                    {section236HNoteText}
                  </p>
                )}
              </div>
              {/* FBR Invoice */}
              {!(formData.invoiceType === "Sale Invoice") && (
                <InputField
                  label="FBR Invoice Ref. No."
                  name="FBRInvoiceRefNo"
                  value={formData.FBRInvoiceRefNo}
                  error={errors.FBRInvoiceRefNo}
                  onChange={handleChange}
                />
              )}
              {/* Debit Note Reason */}
              {formData.invoiceType === "Debit Note" && (
                <InputField
                  label="Debit Note Reason"
                  name="debitNoteReason"
                  value={formData.debitNoteReason}
                  error={errors.debitNoteReason}
                  onChange={handleChange}
                  required
                />
              )}
            </div>

            <h2
              className="text-left font-bold text-xl"
              style={{
                fontFamily: globalStyle.fontFamilyHeading,
                color: globalStyle.curveDarkColor,
              }}
            >
              Invoice Items
            </h2>
            <div className="bg-gray-50 rounded-xl p-4 space-y-6">
              {formData?.items?.map((itemValue, index) => (
                <div
                  key={index}
                  className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm"
                >
                  {/* HEADER */}
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-semibold text-gray-700">
                      Item #{index + 1}
                    </h4>
                  </div>
                  {/* FIELDS */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Item List */}
                    <div>
                      <label className="block mb-1 font-medium text-gray-700">
                        Item <span className="text-red-500">*</span>
                      </label>

                      {/* Opens as a full-width grid of the items. */}
                      <ItemGridSelect
                        name={`items[${index}].itemId`}
                        value={itemValue.itemId}
                        onChange={handleChange}
                        items={itemListAccToCompany}
                        company={invoiceCompany}
                        usedIds={selectedItemIds}
                        disabled={
                          !itemListAccToCompany ||
                          itemListAccToCompany.length === 0
                        }
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-200 focus:outline-none ${
                          errors?.items?.[index]?.itemId
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                      />

                      {errors?.items?.[index]?.itemId && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.items[index].itemId}
                        </p>
                      )}
                    </div>
                    {/* unit Price */}
                    <MoneyInputField
                      label="Unit Price"
                      name={`items[${index}].unitPrice`}
                      value={itemValue.unitPrice}
                      error={errors?.items?.[index]?.unitPrice}
                      onChange={handleChange}
                      required
                    />
                    {/* UOM */}
                    <InputField
                      label="UOM"
                      name={`items[${index}].uoM`}
                      value={itemValue.uoM}
                      error={errors?.items?.[index]?.uoM}
                      onChange={handleChange}
                      disabled={true}
                    />
                    {/* QTY */}
                    <InputField
                      label="QTY"
                      type="number"
                      min={1}
                      name={`items[${index}].quantity`}
                      value={itemValue.quantity}
                      error={errors?.items?.[index]?.quantity}
                      onChange={handleChange}
                      required
                    />
                    {/* Amount */}
                    <InputField
                      label="Amount"
                      type="number"
                      name={`items[${index}].valueSalesExcludingST`}
                      value={displayTwoDecimalDigits(
                        itemValue.valueSalesExcludingST,
                      )}
                      error={errors?.items?.[index]?.valueSalesExcludingST}
                      onChange={handleChange}
                      readOnly={true}
                    />
                    {/* Fixed Notified Value / Retail Price (SN008 / SN027 only) */}
                    {isRetailPriceScenario(formData.transactionType) && (
                      <MoneyInputField
                        label="Fixed Notified Value / Retail Price"
                        min={0}
                        step="0.01"
                        name={`items[${index}].fixedNotifiedValueOrRetailPrice`}
                        value={itemValue.fixedNotifiedValueOrRetailPrice}
                        error={
                          errors?.items?.[index]
                            ?.fixedNotifiedValueOrRetailPrice
                        }
                        onChange={handleChange}
                        required
                      />
                    )}
                    {/* ST % */}
                    <div>
                      <label className="block mb-1 font-medium">
                        ST %<span className="text-red-500">*</span>
                      </label>
                      <SearchableSelect
                        name={`items[${index}].rateValue`}
                        value={findRate(rateList, itemValue)?.ratE_ID ?? ""}
                        onChange={handleChange}
                        disabled={!rateList || rateList.length === 0}
                        className={`w-full px-4 py-2 border rounded-md ${
                          errors?.items?.[index]?.rateValue
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                      >
                        {!rateList || rateList.length === 0 ? (
                          <option value="">No ST% Available</option>
                        ) : (
                          <>
                            <option className="uppercase" value="">
                              SELECT
                            </option>
                            {rateList.map((value) => (
                              <option
                                key={value.ratE_ID}
                                value={value.ratE_ID}
                                data-id={value.ratE_ID}
                              >
                                {value.ratE_DESC}
                              </option>
                            ))}
                          </>
                        )}
                      </SearchableSelect>
                      {errors?.items?.[index]?.rateValue && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors?.items?.[index]?.rateValue}
                        </p>
                      )}
                    </div>
                    {/* ST Amt */}
                    <InputField
                      label="ST Amount"
                      type="number"
                      name={`items[${index}].salesTaxApplicable`}
                      value={displayTwoDecimalDigits(
                        itemValue.salesTaxApplicable,
                      )}
                      error={errors?.items?.[index]?.salesTaxApplicable}
                      readOnly={true}
                    />
                    {/* Further Tax - only for a company that charges it:
                        the item's % of the Amount, before the discount */}
                    {furtherTax.managed && (
                      <InputField
                        label={
                          lineFurtherTaxRate(itemValue)
                            ? `Further Tax @ ${lineFurtherTaxRate(itemValue)}%`
                            : "Further Tax"
                        }
                        type="number"
                        name={`items[${index}].furtherTax`}
                        value={displayTwoDecimalDigits(itemValue.furtherTax)}
                        readOnly={true}
                      />
                    )}
                    {/* Discount — deducted from the total only, the ST above
                        is charged on the undiscounted value */}
                    <MoneyInputField
                      label="Discount"
                      min={0}
                      step="0.01"
                      name={`items[${index}].discount`}
                      value={itemValue.discount}
                      error={errors?.items?.[index]?.discount}
                      onChange={handleChange}
                    />
                    {/* Total Amount  */}
                    <div className="md:col-span-2">
                      <InputField
                        label="Total Amount"
                        type="number"
                        name={`items[${index}].totalValues`}
                        value={displayTwoDecimalDigits(itemValue.totalValues)}
                        error={errors?.items?.[index]?.totalValues}
                        onChange={handleChange}
                        readOnly={true}
                      />
                    </div>
                  </div>
                  {hasRate(itemValue) && (
                    <>
                      {isGettingSroWithRateIdList ||
                      isGettingSroItemsWithSroIdList ? (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: "80px",
                          }}
                        >
                          <CircularProgress size={30} />
                        </Box>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                          {/* SRO Schd. */}
                          {sroList?.length > 0 && (
                            <div>
                              <label className="block mb-1 font-medium">
                                SRO schedule{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <SearchableSelect
                                name={`items[${index}].sroScheduleNo`}
                                value={itemValue.sroScheduleNo}
                                onChange={handleChange}
                                disabled={!sroList || sroList.length === 0}
                                className={`w-full px-4 py-2 border rounded-md ${
                                  errors?.items?.[index]?.sroScheduleNo
                                    ? "border-red-500"
                                    : "border-gray-300"
                                }`}
                              >
                                {!sroList || sroList.length === 0 ? (
                                  <option value="">
                                    No SRO List Available
                                  </option>
                                ) : (
                                  <>
                                    <option className="uppercase" value="">
                                      SELECT
                                    </option>
                                    {sroList.map((value) => (
                                      <option
                                        key={value.serNo}
                                        value={value.srO_DESC}
                                        data-id={value.srO_ID}
                                      >
                                        {value.srO_DESC}
                                      </option>
                                    ))}
                                    {Boolean(itemValue.sroScheduleNo) &&
                                      !sroList.some(
                                        (value) =>
                                          value.srO_DESC ===
                                          itemValue.sroScheduleNo,
                                      ) && (
                                        <option value={itemValue.sroScheduleNo}>
                                          {itemValue.sroScheduleNo}
                                        </option>
                                      )}
                                  </>
                                )}
                              </SearchableSelect>
                              {errors?.items?.[index]?.sroScheduleNo && (
                                <p className="text-red-500 text-sm mt-1">
                                  {errors?.items?.[index]?.sroScheduleNo}
                                </p>
                              )}
                            </div>
                          )}
                          {/* SRO Item */}
                          {sroList?.length > 0 &&
                            (sroItemsList.length > 0 ||
                              Boolean(itemValue.sroItemSerialNo)) && (
                            <div>
                              <label className="block mb-1 font-medium">
                                SRO Item No #{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <SearchableSelect
                                name={`items[${index}].sroItemSerialNo`}
                                value={itemValue.sroItemSerialNo}
                                onChange={handleChange}
                                disabled={
                                  !sroItemsList || sroItemsList.length === 0
                                }
                                className={`w-full px-4 py-2 border rounded-md ${
                                  errors?.items?.[index]?.sroItemSerialNo
                                    ? "border-red-500"
                                    : "border-gray-300"
                                }`}
                              >
                                {!sroItemsList || sroItemsList.length === 0 ? (
                                  <option
                                    value={itemValue.sroItemSerialNo || ""}
                                  >
                                    {itemValue.sroItemSerialNo ||
                                      "No SRO List Available"}
                                  </option>
                                ) : (
                                  <>
                                    <option className="uppercase" value="">
                                      SELECT
                                    </option>
                                    {sroItemsList.map((value) => (
                                      <option
                                        key={value.srO_ITEM_ID}
                                        value={value.srO_ITEM_DESC}
                                        data-id={value.srO_ITEM_ID}
                                      >
                                        {value.srO_ITEM_DESC}
                                      </option>
                                    ))}
                                    {Boolean(itemValue.sroItemSerialNo) &&
                                      !sroItemsList.some(
                                        (value) =>
                                          value.srO_ITEM_DESC ===
                                          itemValue.sroItemSerialNo,
                                      ) && (
                                        <option
                                          value={itemValue.sroItemSerialNo}
                                        >
                                          {itemValue.sroItemSerialNo}
                                        </option>
                                      )}
                                  </>
                                )}
                              </SearchableSelect>
                              {errors?.items?.[index]?.sroItemSerialNo && (
                                <p className="text-red-500 text-sm mt-1">
                                  {errors?.items?.[index]?.sroItemSerialNo}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* ACTION BUTTONS (ONLY LAST ITEM) */}
                  <div className="flex justify-end gap-3 mt-6">
                    {index === formData.items.length - 1 && (
                      <button
                        type="button"
                        onClick={addItem}
                        style={{ backgroundColor: globalStyle.blueColor }}
                        className="px-5 py-2 text-white rounded-lg font-medium hover:opacity-90 transition"
                      >
                        + Add Item
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      style={{ backgroundColor: globalStyle.blueColor }}
                      className="px-5 py-2 text-white rounded-lg font-medium hover:opacity-90 transition"
                    >
                      − Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* SubTotal Portion */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 text-sm">
              {/* Left Section */}
              <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Sales Tax (ST)</span>
                  <span className="font-semibold text-gray-900">
                    {formatTwoDecimals(
                      sumBy(formData?.items, "salesTaxApplicable") || 0,
                    )}
                  </span>
                </div>
                {furtherTaxNoteText && (
                  <p role="status" className="text-xs text-gray-600">
                    {furtherTaxNoteText}
                  </p>
                )}
              </div>
              {/* Right Section */}
              <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">
                    {formatTwoDecimals(
                      sumBy(formData?.items, "valueSalesExcludingST") || 0,
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Net Tax</span>
                  <span className="font-medium">
                    {formatTwoDecimals(
                      sumBy(formData?.items, "salesTaxApplicable") || 0,
                    )}
                  </span>
                </div>
                {furtherTax.managed && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Further Tax</span>
                    <span className="font-medium">
                      {formatTwoDecimals(
                        sumBy(
                          formData?.items,
                          (item) => Number(item?.furtherTax) || 0,
                        ) || 0,
                      )}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Discount</span>
                  <span className="font-medium text-red-600">
                    −
                    {formatTwoDecimals(
                      sumBy(
                        formData?.items,
                        (item) => Number(item?.discount) || 0,
                      ) || 0,
                    )}
                  </span>
                </div>
                <div className="border-t pt-3 mt-2 flex justify-between items-center">
                  <span className="text-base font-semibold text-gray-800">
                    Grand Total
                  </span>
                  <span className="text-lg font-bold text-green-600">
                    {formatTwoDecimals(sumBy(formData?.items, "totalValues") || 0)}
                  </span>
                </div>
                {/* Shown after the invoice total, never added into it */}
                {hasSection236H(formData.GSection236H) && (
                  <>
                    <div className="border-t pt-3 flex justify-between">
                      <span className="text-gray-600">
                        {section236HLabel(formData.GSection236H)}
                      </span>
                      <span className="font-medium">
                        {formatTwoDecimals(section236HAmountFor(
                          sumBy(formData?.items, "totalValues"),
                          formData.GSection236H,
                        ))}
                      </span>
                    </div>
                    <div className="border-t pt-3 flex justify-between items-center">
                      <span className="text-base font-semibold text-gray-800">
                        {SECTION_236H_FINAL_LABEL}
                      </span>
                      <span className="text-lg font-bold text-green-600">
                        {formatTwoDecimals(section236HFinalAmountFor(
                          sumBy(formData?.items, "totalValues"),
                          formData.GSection236H,
                        ))}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* SUBMIT */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              {/* Cancel */}
              <button
                type="button"
                onClick={() => navigate("/main/view-invoice-list")}
                className="w-full py-2.5 rounded-lg font-semibold border border-gray-300 text-gray-700 bg-white hover:bg-gray-100 transition duration-200"
              >
                Cancel
              </button>
              {/* <button
                type="button"
                disabled={isUpdatingInvoicesList}
                onClick={(e) => handleSubmit(e, 2)}
                className="w-full py-2.5 rounded-lg text-white font-semibold transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: globalStyle.buttonColor }}
              >
                {isUpdatingInvoicesList ? "Please Wait..." : "Save & Submit"}
              </button> */}
              <button
                type="button"
                disabled={isUpdatingInvoicesList}
                onClick={(e) => handleSubmit(e, 1)}
                className="w-full py-2.5 rounded-lg text-white font-semibold transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: globalStyle.buttonColor,
                  opacity: 0.9, // slight variation
                }}
              >
                {isUpdatingInvoicesList ? "Please Wait..." : "Save Only"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default EditInvoices;
